import React, { useState, useEffect } from 'react';
import { X, Smartphone, Share } from 'lucide-react';
import toast from 'react-hot-toast';

const PWAInstallPrompt = ({ user }) => {
  const [deferredPrompt, setDeferredPrompt] = useState(null);
  
  useEffect(() => {
    if (!user) return;
    
    // Check if permanently dismissed or already installed
    const permanentlyDismissed = localStorage.getItem('pwaPromptDismissed') === 'permanent';
    const isStandalone = window.matchMedia('(display-mode: standalone)').matches 
      || window.navigator.standalone === true;
    
    if (permanentlyDismissed || isStandalone) return;
    
    // Track login count
    const loginCount = parseInt(localStorage.getItem('loginCount') || '0') + 1;
    localStorage.setItem('loginCount', loginCount.toString());
    
    // Check when we last showed the prompt
    const lastPromptAt = parseInt(localStorage.getItem('pwaPromptLastShown') || '0');
    const loginsSinceLastPrompt = loginCount - lastPromptAt;
    
    // Show prompt after 3 logins initially, then every 5 logins if they clicked "Later"
    const showThreshold = lastPromptAt === 0 ? 3 : 5;
    
    if (loginsSinceLastPrompt < showThreshold) return;
    
    // Detect platform
    const isIOS = /iPad|iPhone|iPod/.test(navigator.userAgent) && !window.MSStream;
    const isAndroid = /Android/.test(navigator.userAgent);
    const isMobile = isIOS || isAndroid;
    
    if (!isMobile) return;
    
    // Record that we're showing the prompt
    localStorage.setItem('pwaPromptLastShown', loginCount.toString());
    
    // Android: Listen for install prompt
    if (isAndroid) {
      const handler = (e) => {
        e.preventDefault();
        setDeferredPrompt(e);
        showAndroidPrompt(e);
      };
      window.addEventListener('beforeinstallprompt', handler);
      return () => window.removeEventListener('beforeinstallprompt', handler);
    }
    
    // iOS: Show custom instructions after a delay
    if (isIOS) {
      const timer = setTimeout(() => showIOSPrompt(), 2000);
      return () => clearTimeout(timer);
    }
  }, [user]);

  // Check if dark mode is active
  const isDarkMode = () => {
    return document.documentElement.classList.contains('dark') || 
           window.matchMedia('(prefers-color-scheme: dark)').matches;
  };

  const handleLater = (toastId) => {
    // Just dismiss - will show again after 5 more logins
    toast.dismiss(toastId);
  };

  const handleNeverAsk = (toastId) => {
    localStorage.setItem('pwaPromptDismissed', 'permanent');
    toast.dismiss(toastId);
  };
  
  const showAndroidPrompt = (promptEvent) => {
    const dark = isDarkMode();
    
    toast((t) => (
      <div className="flex flex-col gap-3 max-w-xs">
        <div className="flex items-center gap-3">
          <div className={`p-2.5 rounded-xl flex-shrink-0 ${dark ? 'bg-purple-900/60' : 'bg-purple-100'}`}>
            <Smartphone size={24} className={dark ? 'text-purple-300' : 'text-purple-600'} />
          </div>
          <div>
            <p className={`font-bold text-base ${dark ? 'text-white' : 'text-black'}`}>Add Boply to Home Screen</p>
            <p className={`text-xs ${dark ? 'text-gray-300' : 'text-gray-600'}`}>Get the app experience</p>
          </div>
          <button 
            onClick={() => handleLater(t.id)} 
            className={`flex-shrink-0 ml-auto ${dark ? 'text-gray-400 hover:text-gray-200' : 'text-gray-500 hover:text-gray-700'}`}
          >
            <X size={20} />
          </button>
        </div>
        
        <ul className={`text-sm space-y-1.5 pl-1 ${dark ? 'text-gray-200' : 'text-gray-700'}`}>
          <li className="flex items-center gap-2">
            <span className="text-green-500">✓</span> Launch instantly from your home screen
          </li>
          <li className="flex items-center gap-2">
            <span className="text-green-500">✓</span> Full screen — no browser bars
          </li>
          <li className="flex items-center gap-2">
            <span className="text-green-500">✓</span> Faster load times
          </li>
        </ul>
        
        <div className="flex gap-2 mt-1">
          <button
            onClick={() => {
              promptEvent.prompt();
              promptEvent.userChoice.then((choice) => {
                if (choice.outcome === 'accepted') {
                  toast.success('🎉 Boply added to home screen!');
                  localStorage.setItem('pwaPromptDismissed', 'permanent');
                }
                setDeferredPrompt(null);
              });
              toast.dismiss(t.id);
            }}
            className="flex-1 py-2.5 bg-purple-600 hover:bg-purple-700 text-white text-sm font-semibold rounded-xl transition-colors"
          >
            Add to Home Screen
          </button>
          <button
            onClick={() => handleLater(t.id)}
            className={`px-4 py-2.5 text-sm font-medium rounded-xl transition-colors ${dark ? 'bg-gray-700 text-gray-200 hover:bg-gray-600' : 'bg-gray-200 text-gray-700 hover:bg-gray-300'}`}
          >
            Later
          </button>
        </div>
        
        <button
          onClick={() => handleNeverAsk(t.id)}
          className={`text-xs text-center ${dark ? 'text-gray-500 hover:text-gray-400' : 'text-gray-400 hover:text-gray-500'}`}
        >
          Don't ask again
        </button>
      </div>
    ), {
      duration: 20000,
      position: 'bottom-center',
      style: {
        background: dark ? '#1f2937' : 'white',
        padding: '16px',
        borderRadius: '16px',
        boxShadow: '0 10px 40px rgba(0,0,0,0.3)',
        maxWidth: '340px',
      },
    });
  };
  
  const showIOSPrompt = () => {
    const dark = isDarkMode();
    
    toast((t) => (
      <div className="flex flex-col gap-3 max-w-xs">
        <div className="flex items-center gap-3">
          <div className={`p-2.5 rounded-xl flex-shrink-0 ${dark ? 'bg-purple-900/60' : 'bg-purple-100'}`}>
            <Smartphone size={24} className={dark ? 'text-purple-300' : 'text-purple-600'} />
          </div>
          <div>
            <p className={`font-bold text-base ${dark ? 'text-white' : 'text-black'}`}>Add Boply to Home Screen</p>
            <p className={`text-xs ${dark ? 'text-gray-300' : 'text-gray-600'}`}>Get the app experience</p>
          </div>
          <button 
            onClick={() => handleLater(t.id)} 
            className={`flex-shrink-0 ml-auto ${dark ? 'text-gray-400 hover:text-gray-200' : 'text-gray-500 hover:text-gray-700'}`}
          >
            <X size={20} />
          </button>
        </div>
        
        <ul className={`text-sm space-y-1.5 pl-1 ${dark ? 'text-gray-200' : 'text-gray-700'}`}>
          <li className="flex items-center gap-2">
            <span className="text-green-500">✓</span> Launch instantly from your home screen
          </li>
          <li className="flex items-center gap-2">
            <span className="text-green-500">✓</span> Full screen — no browser bars
          </li>
          <li className="flex items-center gap-2">
            <span className="text-green-500">✓</span> Faster load times
          </li>
        </ul>
        
        <div className={`rounded-xl p-3 mt-1 ${dark ? 'bg-gray-800' : 'bg-gray-100'}`}>
          <p className={`text-sm font-medium mb-2 ${dark ? 'text-white' : 'text-gray-800'}`}>How to install:</p>
          <ol className={`text-sm space-y-2 ${dark ? 'text-gray-300' : 'text-gray-700'}`}>
            <li className="flex items-start gap-2">
              <span className="w-5 h-5 bg-purple-600 text-white rounded-full flex items-center justify-center text-xs font-bold flex-shrink-0">1</span>
              <span>Tap <Share size={16} className="inline mx-1 text-blue-500" /> at the bottom of Safari</span>
            </li>
            <li className="flex items-start gap-2">
              <span className="w-5 h-5 bg-purple-600 text-white rounded-full flex items-center justify-center text-xs font-bold flex-shrink-0">2</span>
              <span>Scroll down and tap <strong>"Add to Home Screen"</strong></span>
            </li>
          </ol>
        </div>
        
        <button
          onClick={() => handleLater(t.id)}
          className={`w-full py-2.5 text-sm font-medium rounded-xl transition-colors ${dark ? 'bg-gray-700 text-gray-200 hover:bg-gray-600' : 'bg-gray-200 text-gray-700 hover:bg-gray-300'}`}
        >
          Maybe Later
        </button>
        
        <button
          onClick={() => handleNeverAsk(t.id)}
          className={`text-xs text-center ${dark ? 'text-gray-500 hover:text-gray-400' : 'text-gray-400 hover:text-gray-500'}`}
        >
          Don't ask again
        </button>
      </div>
    ), {
      duration: 25000,
      position: 'bottom-center',
      style: {
        background: dark ? '#1f2937' : 'white',
        padding: '16px',
        borderRadius: '16px',
        boxShadow: '0 10px 40px rgba(0,0,0,0.3)',
        maxWidth: '340px',
      },
    });
  };
  
  return null;
};

export default PWAInstallPrompt;